import React, { useLayoutEffect, useRef } from 'react';
import { Backdrop, Box, IconButton, Toolbar, Unstable_TrapFocus as FocusTrap } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Panel, Group as PanelGroup } from 'react-resizable-panels';
import type { PanelImperativeHandle, PanelSize } from 'react-resizable-panels';
import { ResizeHandle } from '../../../shared/components/ResizeHandle';
import type { SidebarLayout, SidebarSide } from '../hooks/useSidebarLayout';
import { SHEET_ID, otherModalOpen } from '../hooks/sheetFocus';
import { useSheetFocusRestore } from '../hooks/useSheetFocusRestore';
import { useStrandedFocusWatch } from '../hooks/useStrandedFocusWatch';

/** Panel ids, stable at every width: the group keys its layout on them. */
const PANEL_ID = {
  left: 'investigation-left',
  main: 'investigation-main',
  right: 'investigation-right',
} as const;

const SHEET = {
  left: { label: 'Investigations', close: 'Close investigations', width: '85vw', maxWidth: 320 },
  right: { label: 'Investigation data', close: 'Close investigation data', width: '90vw', maxWidth: 380 },
} as const;

/** The trap's `isEnabled`: module level, so the trap's listeners are not re-added on every render. */
const trapEnabled = () => !otherModalOpen();

const TABBABLE = 'input, select, textarea, a[href], button, [tabindex], [contenteditable]:not([contenteditable="false"])';

/** Hidden by display or visibility anywhere from the node up to the sheet; jsdom has no layout to ask. */
function isShown(node: HTMLElement, root: HTMLElement): boolean {
  for (let el: HTMLElement | null = node; el && el !== root; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

/**
 * The sheet's Tab stops in document order: the trap wraps to the first and last of these, so a control a
 * collapsed section hides is never where focus lands.
 */
function visibleTabbable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE)).filter((node) => (
    node.tabIndex >= 0
    && !(node as HTMLButtonElement).disabled
    && !(node instanceof HTMLInputElement && node.type === 'hidden')
    && isShown(node, root)
  ));
}

interface InvestigationLayoutProps {
  /** Below 900px the side panels become sheets over the chat. */
  isCompact: boolean;
  /** Open state from `useSidebarLayout`; this component only draws it. */
  layout: SidebarLayout;
  left: React.ReactNode;
  main: React.ReactNode;
  right: React.ReactNode;
}

interface SideHostProps {
  side: SidebarSide;
  isCompact: boolean;
  panelOpen: boolean;
  sheetOpen: boolean;
  /** The other side's sheet is open: this one closed to make way, and focus belongs to that one. */
  otherSheetOpen: boolean;
  /** Focus went back to this opener outside the sheet; the layout watches that it stays somewhere usable. */
  onRestored: (side: SidebarSide, opener: HTMLElement) => void;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * One side's content. From md it fills its panel; on compact the same element turns into a sheet fixed
 * to the screen edge, so the content inside is never remounted by a change of width. The open sheet holds
 * focus in a trap, which yields while any modal layer is showing (dialogs, menus, popovers), whether it was
 * opened from inside the sheet or from elsewhere. Escape reaches the sheet only once those layers are gone.
 */
const SideHost: React.FC<SideHostProps> = ({
  side, isCompact, panelOpen, sheetOpen, otherSheetOpen, onRestored, onClose, children,
}) => {
  const sheet = SHEET[side];
  const offscreen = side === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (otherModalOpen()) return;
    if (isCompact && sheetOpen && event.key === 'Escape' && !event.defaultPrevented) onClose();
  };

  const trapOpen = isCompact && sheetOpen;
  useSheetFocusRestore({ side, isCompact, trapOpen, otherSheetOpen, onRestored });

  return (
    <FocusTrap open={trapOpen} getTabbable={visibleTabbable} isEnabled={trapEnabled} disableRestoreFocus>
      <Box
        id={SHEET_ID[side]}
        onKeyDown={onKeyDown}
        data-mode={isCompact ? 'sheet' : 'panel'}
        role={trapOpen ? 'dialog' : undefined}
        aria-modal={trapOpen ? true : undefined}
        aria-label={isCompact ? sheet.label : undefined}
        tabIndex={isCompact ? -1 : undefined}
        inert={isCompact && !sheetOpen}
        sx={isCompact
          ? (theme) => ({
            position: 'fixed',
            top: 0,
            bottom: 0,
            [side]: 0,
            width: sheet.width,
            maxWidth: sheet.maxWidth,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            boxShadow: 16,
            zIndex: theme.zIndex.drawer,
            outline: 'none',
            transform: sheetOpen ? 'none' : offscreen,
            visibility: sheetOpen ? 'visible' : 'hidden',
            // Hidden only once the slide out has finished, so the sheet is seen leaving.
            transition: sheetOpen
              ? theme.transitions.create('transform', { duration: theme.transitions.duration.enteringScreen })
              : `${theme.transitions.create('transform', { duration: theme.transitions.duration.leavingScreen })}, `
                + `visibility 0s linear ${theme.transitions.duration.leavingScreen}ms`,
          })
          : { height: '100%', width: '100%', display: panelOpen ? 'flex' : 'none', flexDirection: 'column', minHeight: 0 }}
      >
        {/* The app bar stays above the sheet; this keeps the content below it. */}
        {isCompact && <Toolbar />}
        {/* Touch screen readers send no Escape, the scrim sits outside the dialog and the header is inert. */}
        {isCompact && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 0.5 }}>
            <IconButton aria-label={sheet.close} onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      </Box>
    </FocusTrap>
  );
};

/**
 * The investigation screen's three columns: the investigation list, the chat and the investigation data.
 * Every width renders the same tree, one panel group with all three panels, so turning a tablet across 900px
 * keeps the chat's draft, scroll position and edit state and the sidebars' own state. From 900px the side
 * panels resize and collapse; below it they hide in the group and their content slides in as a sheet.
 */
const InvestigationLayout: React.FC<InvestigationLayoutProps> = ({ isCompact, layout, left, main, right }) => {
  const { panelOpen, sheetOpen, closeSheet, setPanelOpen } = layout;
  const leftPanel = useRef<PanelImperativeHandle | null>(null);
  const rightPanel = useRef<PanelImperativeHandle | null>(null);

  let openSheet: SidebarSide | null = null;
  if (isCompact && sheetOpen.left) openSheet = 'left';
  else if (isCompact && sheetOpen.right) openSheet = 'right';

  const onRestored = useStrandedFocusWatch(isCompact, openSheet);

  // The panels follow the open state from md. A layout effect, so a panel the group squeezed shut while
  // compact is expanded again before the group reports its size back through onResize.
  useLayoutEffect(() => {
    if (isCompact) return;
    for (const [panel, open] of [[leftPanel.current, panelOpen.left], [rightPanel.current, panelOpen.right]] as const) {
      if (!panel) continue;
      if (open && panel.isCollapsed()) panel.expand();
      if (!open && !panel.isCollapsed()) panel.collapse();
    }
  }, [isCompact, panelOpen.left, panelOpen.right]);

  // A panel dragged shut or open from md moves its toggle with it. Compact sizes are the group's own
  // business: the panels are hidden there and their open state has to survive the trip.
  const onResize = (side: SidebarSide) => (size: PanelSize) => {
    if (!isCompact) setPanelOpen(side, size.asPercentage > 0);
  };

  const hidden = { display: 'none !important' };
  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: 0,
        display: 'flex',
        // The library sets each panel's size inline, so hiding a panel has to outrank it.
        ...(isCompact
          ? {
            '& [data-separator]': hidden,
            [`& #${PANEL_ID.left}, & #${PANEL_ID.right}`]: { flexGrow: '0 !important', flexBasis: '0px !important' },
          }
          : {
            ...(!panelOpen.left && { [`& #${PANEL_ID.left} + [data-separator]`]: hidden }),
            ...(!panelOpen.right && { [`& #${PANEL_ID.main} + [data-separator]`]: hidden }),
          }),
      }}
    >
      <PanelGroup orientation="horizontal" style={{ flexGrow: 1 }}>
        <Panel
          id={PANEL_ID.left}
          panelRef={leftPanel}
          collapsible
          collapsedSize={0}
          defaultSize={260}
          minSize={240}
          maxSize="35%"
          onResize={onResize('left')}
        >
          <SideHost side="left" isCompact={isCompact} panelOpen={panelOpen.left} sheetOpen={sheetOpen.left} otherSheetOpen={openSheet === 'right'} onRestored={onRestored} onClose={() => closeSheet('left')}>
            {left}
          </SideHost>
        </Panel>
        <ResizeHandle />
        <Panel id={PANEL_ID.main} minSize={340}>
          <Box inert={openSheet !== null} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {main}
          </Box>
        </Panel>
        <ResizeHandle />
        <Panel
          id={PANEL_ID.right}
          panelRef={rightPanel}
          collapsible
          collapsedSize={0}
          defaultSize="30%"
          minSize={300}
          maxSize="40%"
          onResize={onResize('right')}
        >
          <SideHost side="right" isCompact={isCompact} panelOpen={panelOpen.right} sheetOpen={sheetOpen.right} otherSheetOpen={openSheet === 'left'} onRestored={onRestored} onClose={() => closeSheet('right')}>
            {right}
          </SideHost>
        </Panel>
      </PanelGroup>

      <Backdrop
        open={openSheet !== null}
        onClick={() => openSheet && closeSheet(openSheet)}
        data-testid="investigation-sheet-scrim"
        sx={(theme) => ({ zIndex: theme.zIndex.drawer - 1 })}
      />
    </Box>
  );
};

export default InvestigationLayout;
